/**
 * address-manager.js
 * ══════════════════════════════════════════════════════════════════
 * Módulo global de gestión de direcciones para Vitalea Ecommerce.
 *
 * RESPONSABILIDADES:
 *  1. Fuente única de verdad para direcciones (usuarios autenticados
 *     y visitantes / invitados).
 *  2. Persistencia en localStorage para invitados.
 *  3. Sincronización localStorage → Shopify account al iniciar sesión.
 *  4. CRUD: listar, crear, editar, eliminar, seleccionar.
 *  5. Modal de UI (lista + formulario) integrado en el flujo de
 *     agendamiento a domicilio.
 *  6. Selección global accesible desde cualquier flujo del sitio.
 *
 * USO:
 *  window.AddressManager.open()                → abre el modal de gestión
 *  window.AddressManager.getSelected()         → retorna la dirección activa
 *  window.AddressManager.onSelect(fn)          → suscribe un callback al cambio
 *  window.AddressManager.syncOnLogin()         → llama tras un login exitoso
 *  window.AddressManager.setCiudadFiltro(str)  → filtra modal por ciudad activa
 *
 * DEPENDENCIAS:
 *  - SweetAlert2 (Swal) ya cargado en la página.
 *  - window.CartAgendaConfig.middlewareBaseUrl  (opcional, para depts.)
 *  - CSS del modal inyectado en el <head> por este mismo script.
 * ══════════════════════════════════════════════════════════════════
 */

(function (global) {
  'use strict';

  /* ─── Constantes ─────────────────────────────────────────────── */
  const LS_KEY_ADDRESSES  = 'vtl_addresses';
  const LS_KEY_SELECTED   = 'vtl_address_selected';
  const SHOPIFY_ADDR_URL  = '/account/addresses';
  const COLOMBIA_COUNTRY  = 'Colombia';

  /* ─── Estado interno ─────────────────────────────────────────── */
  let _addresses      = [];   // array canónico en memoria
  let _selectedId     = null; // id de la dirección activa
  let _subscribers    = [];   // callbacks suscritos a onSelect
  let _modalMounted   = false;
  let _departaLoaded  = false;
  let _currentEditId  = null; // id siendo editado (null = nueva)
  let _initDone       = false; // FIX: evita _notify() durante la carga inicial
  let _ciudadFiltro   = null; // FIX: ciudad normalizada para filtrar el modal

  /* ─── Catálogos del middleware (departamentos y ciudades) ───────
     C4: con tiempo límite, en caché por sesión de página y con aviso
     visible si fallan. Antes cada carga hacía su propio fetch y un error
     solo quedaba en la consola con el selector vacío. */
  const MW_TIMEOUT_MS = 15000;
  const _catalogos    = new Map(); // url -> Promise<array>

  function _mwCatalogo(url) {
    if (_catalogos.has(url)) return _catalogos.get(url);
    const ctrl  = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), MW_TIMEOUT_MS);
    const p = fetch(url, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(data => Array.isArray(data) ? data : [])
      .catch(err => { _catalogos.delete(url); throw err; }) // un fallo no queda en caché
      .finally(() => clearTimeout(timer));
    _catalogos.set(url, p);
    return p;
  }

  const _departamentos = () =>
    _mwCatalogo(`${middlewareBaseUrl}ajax/genericConsult.php?action=consultDepartment`);
  const _ciudades = deptId =>
    _mwCatalogo(`${middlewareBaseUrl}ajax/genericConsult.php?action=consultCitDepartment&departament_Id=${encodeURIComponent(deptId)}`);

  /* Aviso bajo el selector con botón para reintentar; sin mensaje lo quita */
  function _avisoCatalogo(sel, mensaje, reintentar) {
    let aviso = sel.parentElement.querySelector('.vtl-aviso-catalogo');
    if (!mensaje) { if (aviso) aviso.remove(); return; }
    if (!aviso) {
      aviso = document.createElement('div');
      aviso.className = 'vtl-aviso-catalogo';
      aviso.style.cssText = 'font-size:13px;color:#c2413a;margin-top:6px';
      sel.insertAdjacentElement('afterend', aviso);
    }
    aviso.innerHTML = `${mensaje} <button type="button" style="color:#e71578;font-weight:600;background:none;border:0;cursor:pointer;padding:0">Reintentar</button>`;
    aviso.querySelector('button').onclick = () => { _avisoCatalogo(sel, ''); reintentar(); };
  }

  /* Llena el selector de departamentos y luego ejecuta despues() */
  function _cargarDepartamentos(sel, despues) {
    _departamentos()
      .then(data => {
        sel.innerHTML = '<option value="">Seleccione el departamento</option>';
        data.forEach(item => {
          const opt       = document.createElement('option');
          opt.value       = item.id;
          opt.textContent = item.depto;
          sel.appendChild(opt);
        });
        _departaLoaded = true;
        _avisoCatalogo(sel, '');
        despues();
      })
      .catch(err => {
        console.error('[AddressManager] Departamentos:', err);
        _avisoCatalogo(sel, 'No pudimos cargar los departamentos.', () => _cargarDepartamentos(sel, despues));
      });
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 1 — PERSISTENCIA Y FUENTE DE DATOS
      ═══════════════════════════════════════════════════════════════ */

  /** Detecta si las direcciones se guardan en la cuenta de Shopify.
   *  Solo con cuentas clásicas: /account/addresses no existe con cuentas
   *  nuevas (código por correo), y ahí se guardan en el navegador como
   *  para un cliente sin sesión. */
  function _isLoggedIn() {
    if (global.VTL_CUENTAS_NUEVAS) return false;
    const cfg = global.CartAgendaConfig || {};
    return !!(cfg.customerId && String(cfg.customerId) !== '0' && String(cfg.customerId) !== '');
  }

  /** Genera un id único para direcciones locales */
  function _uid() {
    return 'local_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
  }

  /** Lee el array de direcciones desde localStorage (invitados) */
  function _lsGet() {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY_ADDRESSES) || '[]');
    } catch (_) { return []; }
  }

  /** Guarda el array en localStorage */
  function _lsSet(arr) {
    try { localStorage.setItem(LS_KEY_ADDRESSES, JSON.stringify(arr)); } catch (_) {}
  }

  /** Lee el id seleccionado desde localStorage */
  function _lsGetSelected() {
    return localStorage.getItem(LS_KEY_SELECTED) || null;
  }

  /** Guarda el id seleccionado en localStorage */
  function _lsSetSelected(id) {
    if (id) localStorage.setItem(LS_KEY_SELECTED, id);
    else localStorage.removeItem(LS_KEY_SELECTED);
  }

  /**
   * Convierte una dirección de Shopify (objeto address del tema)
   * al formato canónico interno.
   */
  function _shopifyToCanonical(shopifyAddr) {
    return {
      id:        'shopify_' + shopifyAddr.id,
      shopifyId:  shopifyAddr.id,
      label:      shopifyAddr.company || '',
      address1:   shopifyAddr.address1 || '',
      address2:   shopifyAddr.address2 || '',
      city:       shopifyAddr.city || '',
      province:   shopifyAddr.province || '',
      country:    shopifyAddr.country || COLOMBIA_COUNTRY,
      zip:        shopifyAddr.zip || '',
      isDefault:  shopifyAddr.default || false,
      source:     'shopify',
    };
  }

  /**
   * Convierte una dirección canónica interna al formato de form
   * que acepta la API de Shopify customer_address.
   */
  function _canonicalToShopifyForm(addr) {
    return {
      'address[first_name]':  global.CartAgendaConfig?.customerFirstName || '',
      'address[last_name]':   global.CartAgendaConfig?.customerLastName  || '',
      'address[company]':     addr.label,
      'address[address1]':    addr.address1,
      'address[address2]':    addr.address2,
      'address[city]':        addr.city,
      'address[province]':    addr.province,
      'address[country]':     COLOMBIA_COUNTRY,
      'address[zip]':         addr.zip || '',
    };
  }

  /* ─── Carga inicial de direcciones ──────────────────────────── */

  async function _loadAddresses() {
    if (_isLoggedIn()) {
      const cfg = global.CartAgendaConfig || {};

      if (Array.isArray(cfg.customerAddresses) && cfg.customerAddresses.length >= 0) {
        _addresses = cfg.customerAddresses.map(_shopifyToCanonical);
      } else {
        try {
          const res  = await fetch('/account?view=addresses_json', { headers: { Accept: 'application/json' } });
          const data = await res.json();
          _addresses = (data.addresses || []).map(_shopifyToCanonical);
        } catch (_) {
          _addresses = [];
        }
      }

      const savedId = _lsGetSelected();
      _selectedId   = _addresses.find(a => a.id === savedId) ? savedId
                      : (_addresses.find(a => a.isDefault)?.id || _addresses[0]?.id || null);
    } else {
      _addresses  = _lsGet();
      _selectedId = _lsGetSelected();
      if (!_addresses.find(a => a.id === _selectedId)) {
        _selectedId = _addresses[0]?.id || null;
      }
    }
    _lsSetSelected(_selectedId);
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 2 — SINCRONIZACIÓN LOCAL → SHOPIFY
      ═══════════════════════════════════════════════════════════════ */

  async function syncOnLogin() {
    const localAddresses = _lsGet().filter(a => a.source === 'local');
    if (!localAddresses.length) {
      await _loadAddresses();
      _notify();
      return;
    }

    await _loadAddresses();
    const existing = _addresses.filter(a => a.source === 'shopify');

    const isDuplicate = (localAddr) =>
      existing.some(e =>
        _normalize(e.address1) === _normalize(localAddr.address1) &&
        _normalize(e.city)     === _normalize(localAddr.city)     &&
        _normalize(e.province) === _normalize(localAddr.province)
      );

    const toUpload = localAddresses.filter(a => !isDuplicate(a));

    for (const addr of toUpload) {
      try {
        await _createShopifyAddress(addr);
      } catch (e) { console.warn('[AddressManager] sync error', e); }
    }

    localStorage.removeItem(LS_KEY_ADDRESSES);
    await _loadAddresses();
    _notify();
  }

  function _normalize(str) {
    return (str || '').toLowerCase().trim().replace(/\s+/g, ' ');
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 3 — OPERACIONES CRUD
      ═══════════════════════════════════════════════════════════════ */

  function getAll() { return [..._addresses]; }

  function getSelected() {
    return _addresses.find(a => a.id === _selectedId) || null;
  }

  function select(id) {
    if (!_addresses.find(a => a.id === id)) return;
    _selectedId = id;
    _lsSetSelected(id);
    _notify();
  }

  async function create(formData) {
    const addr = _buildCanonical(formData);

    if (_isLoggedIn()) {
      const shopifyId = await _createShopifyAddress(addr);
      addr.id        = 'shopify_' + shopifyId;
      addr.shopifyId  = shopifyId;
      addr.source     = 'shopify';
    } else {
      addr.id     = _uid();
      addr.source = 'local';
      const arr   = _lsGet();
      arr.push(addr);
      _lsSet(arr);
    }

    _addresses.push(addr);
    return addr;
  }

  async function update(id, formData) {
    const idx = _addresses.findIndex(a => a.id === id);
    if (idx === -1) throw new Error('Dirección no encontrada');

    const updated = { ..._addresses[idx], ..._buildCanonical(formData), id };

    if (_isLoggedIn() && updated.shopifyId) {
      await _updateShopifyAddress(updated.shopifyId, updated);
    } else {
      const arr     = _lsGet();
      const lsIdx   = arr.findIndex(a => a.id === id);
      if (lsIdx > -1) { arr[lsIdx] = updated; _lsSet(arr); }
    }

    _addresses[idx] = updated;
    return updated;
  }

  async function remove(id) {
    const addr = _addresses.find(a => a.id === id);
    if (!addr) return;

    if (_isLoggedIn() && addr.shopifyId) {
      await _deleteShopifyAddress(addr.shopifyId);
    } else {
      const arr = _lsGet().filter(a => a.id !== id);
      _lsSet(arr);
    }

    _addresses = _addresses.filter(a => a.id !== id);
    if (_selectedId === id) {
      _selectedId = _addresses[0]?.id || null;
      _lsSetSelected(_selectedId);
    }
    _notify();
  }

  /* ─── Helpers Shopify REST ───────────────────────────────────── */

  async function _createShopifyAddress(addr) {
    const body = new URLSearchParams({
      form_type: 'customer_address',
      utf8:      '✓',
      ..._canonicalToShopifyForm(addr),
    });
    const res = await fetch(SHOPIFY_ADDR_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:    body.toString(),
    });
    if (!res.ok) throw new Error('Error al crear dirección en Shopify');
    return 'pending_' + Date.now();
  }

  async function _updateShopifyAddress(shopifyId, addr) {
    const body = new URLSearchParams({
      form_type: 'customer_address',
      utf8:      '✓',
      _method:   'put',
      ..._canonicalToShopifyForm(addr),
    });
    const res = await fetch(`${SHOPIFY_ADDR_URL}/${shopifyId}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:    body.toString(),
    });
    if (!res.ok) throw new Error('Error al actualizar dirección en Shopify');
  }

  async function _deleteShopifyAddress(shopifyId) {
    const body = new URLSearchParams({ form_type: 'customer_address', _method: 'delete' });
    const res = await fetch(`${SHOPIFY_ADDR_URL}/${shopifyId}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:    body.toString(),
    });
    if (!res.ok) throw new Error('Error al eliminar dirección en Shopify');
  }

  /* ─── Constructor canónico ───────────────────────────────────── */
  function _buildCanonical(f) {
    return {
      id:        '',
      shopifyId: null,
      label:     (f.label     || '').trim(),
      address1:  (f.address1  || '').trim(),
      address2:  (f.address2  || '').trim(),
      city:      (f.city      || '').trim(),
      province:  (f.province  || '').trim(),
      country:   COLOMBIA_COUNTRY,
      zip:       (f.zip       || '').trim(),
      isDefault: !!f.isDefault,
      source:    'local',
    };
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 4 — SUSCRIPTORES
      ═══════════════════════════════════════════════════════════════ */

  function onSelect(fn) {
    if (typeof fn === 'function') _subscribers.push(fn);
  }

  function offSelect(fn) {
    _subscribers = _subscribers.filter(s => s !== fn);
  }

  /**
   * FIX: _notify() no dispara durante la carga inicial (_initDone === false).
   * Esto evita que el chip de dirección aparezca antes de que el usuario
   * seleccione un horario.
   */
  function _notify() {
    if (!_initDone) return;
    const sel = getSelected();
    _subscribers.forEach(fn => { try { fn(sel); } catch (_) {} });
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 4b — FILTRO DE CIUDAD PARA EL MODAL
      ═══════════════════════════════════════════════════════════════ */

  /**
   * Normaliza un string para comparaciones: mayúsculas, sin tildes,
   * sin espacios dobles.
   */
  function _normCity(str) {
    return (str || '').toUpperCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  /**
   * Establece la ciudad activa del agendamiento para filtrar el modal.
   * Llamar con null para limpiar el filtro y mostrar todas las direcciones.
   *
   * @param {string|null} ciudad - Nombre de la ciudad (sin normalizar)
   */
  function setCiudadFiltro(ciudad) {
    _ciudadFiltro = ciudad ? _normCity(ciudad) : null;
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 5 — MODAL UI
      ═══════════════════════════════════════════════════════════════ */

  async function open() {
    if (!_modalMounted) _mountModal();
    await _loadAddresses();
    _renderList();
    document.getElementById('vtl-addr-modal').style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  function _close() {
    const modal = document.getElementById('vtl-addr-modal');
    if (modal) modal.style.display = 'none';
    document.body.style.overflow = '';
    _currentEditId = null;
  }

  /* ─── Render lista ───────────────────────────────────────────── */
  function _renderList() {
    const view     = document.getElementById('vtl-addr-list-view');
    const formView = document.getElementById('vtl-addr-form-view');
    if (!view || !formView) return;

    formView.style.display = 'none';
    view.style.display     = 'block';

    const container = document.getElementById('vtl-addr-list');
    if (!container) return;

    /* FIX: filtra por ciudad activa si hay un filtro establecido */
    const listaFiltrada = _ciudadFiltro
      ? _addresses.filter(a => _normCity(a.city) === _ciudadFiltro)
      : _addresses;

    const hayFiltro = !!_ciudadFiltro;

    if (listaFiltrada.length === 0) {
      container.innerHTML = `
        <div class="vtl-addr-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                  stroke="var(--cart-pink)" stroke-width="1.5" fill="none"/>
            <circle cx="12" cy="9" r="2.5" stroke="var(--cart-pink)" stroke-width="1.5" fill="none"/>
          </svg>
          <p>${hayFiltro
            ? 'No tienes direcciones guardadas en esta ciudad. Agrega una nueva.'
            : 'Aún no tienes direcciones guardadas.'}</p>
        </div>`;
      return;
    }

    container.innerHTML = listaFiltrada.map(addr => `
      <div class="vtl-addr-card ${addr.id === _selectedId ? 'vtl-addr-card--selected' : ''}"
           data-id="${addr.id}">
        <div class="vtl-addr-card__radio">
          <div class="vtl-addr-radio ${addr.id === _selectedId ? 'vtl-addr-radio--active' : ''}"></div>
        </div>
        <div class="vtl-addr-card__body">
          <strong class="vtl-addr-card__label">
            ${_esc(addr.label || 'Mi dirección')}
            ${addr.isDefault ? '<span class="vtl-addr-badge">Principal</span>' : ''}
          </strong>
          <span class="vtl-addr-card__text">${_esc(_formatAddr(addr))}</span>
        </div>
        <div class="vtl-addr-card__actions">
          <button class="vtl-addr-btn-icon" data-action="edit" data-id="${addr.id}" title="Editar">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <button class="vtl-addr-btn-icon vtl-addr-btn-icon--danger" data-action="delete"
                  data-id="${addr.id}" title="Eliminar">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    `).join('');

    container.querySelectorAll('.vtl-addr-card').forEach(card => {
      card.addEventListener('click', e => {
        const btn = e.target.closest('[data-action]');
        if (btn) {
          e.stopPropagation();
          const id = btn.dataset.id;
          if (btn.dataset.action === 'edit')   { _openForm(id); return; }
          if (btn.dataset.action === 'delete') { _confirmDelete(id); return; }
        }
        const id = card.dataset.id;
        select(id);
        _renderList();
      });
    });
  }

  /* ─── Render formulario ──────────────────────────────────────── */
  function _openForm(editId = null) {
    _currentEditId = editId;
    const view     = document.getElementById('vtl-addr-list-view');
    const formView = document.getElementById('vtl-addr-form-view');
    const title    = document.getElementById('vtl-addr-form-title');
    if (!formView) return;

    view.style.display     = 'none';
    formView.style.display = 'block';
    if (title) title.textContent = editId ? 'Editar dirección' : 'Nueva dirección';

    const form = document.getElementById('vtl-addr-form');
    if (!form) return;
    form.reset();

    // Limpia ciudad y error cada vez que se abre el form
    const cityWrap = document.getElementById('vtl-f-city-wrap');
    const citySel  = document.getElementById('vtl-f-city');
    const errorEl  = document.getElementById('vtl-addr-form-error');
    if (cityWrap) cityWrap.style.display = 'none';
    if (citySel)  citySel.innerHTML = '<option value="">Seleccione una ciudad</option>';
    if (errorEl)  errorEl.style.display = 'none';

    // Limpia hidden prefill inputs
    const provPrefill  = document.getElementById('vtl-f-prov-prefill');
    const cityPrefill  = document.getElementById('vtl-f-city-prefill');
    if (provPrefill) provPrefill.setAttribute('data-value', '');
    if (cityPrefill) cityPrefill.setAttribute('data-value', '');

    if (editId) {
      const addr = _addresses.find(a => a.id === editId);
      if (addr) _fillForm(addr);
      _loadDepartamentosForModal();
    } else {
      _loadDepartamentosForModal_thenPrefill();
    }
  }

  function _loadDepartamentosForModal_thenPrefill() {
    const sel = document.getElementById('vtl-f-province');
    if (!sel) return;

    if (_departaLoaded && sel.options.length > 1) {
      sel.value = '';
      _prefillFromAgendamiento();
      return;
    }

    _cargarDepartamentos(sel, _prefillFromAgendamiento);
  }

  function _prefillFromAgendamiento() {
    const selCP = document.querySelector('.agendamiento-select[bandera="CP"] select');
    if (!selCP || selCP.selectedIndex <= 0) return;

    const ciudadAgendamiento = (
      selCP.selectedOptions[0].getAttribute('nombresede') ||
      selCP.selectedOptions[0].textContent?.trim()
    );
    if (!ciudadAgendamiento || ciudadAgendamiento.startsWith('Selecciona')) return;

    const normalizar = str => str.toUpperCase().trim().replace(/\s+/g, ' ')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const ciudadNorm = normalizar(ciudadAgendamiento);

    function buscarCiudadEnDepartamentos(intentos) {
      const provSelect = document.getElementById('vtl-f-province');
      if (!provSelect || provSelect.options.length <= 1) {
        if (intentos > 0) setTimeout(() => buscarCiudadEnDepartamentos(intentos - 1), 300);
        return;
      }

      const deptoOptions = [...provSelect.options].filter(o => o.value);

      async function probarDepto(opt) {
        try {
          const data = await _ciudades(opt.value);

          const match = data.find(item => normalizar(item.ciudad) === ciudadNorm);
          if (!match) return false;

          provSelect.value = opt.value;
          provSelect.dispatchEvent(new Event('change'));
          await _esperarCiudadesYSeleccionar(ciudadNorm);
          return true;
        } catch (_) {
          return false;
        }
      }

      (async () => {
        const mapaRapido = {
          'BOGOTA, D.C.': 'BOGOTA',
          'BOGOTA D.C.':  'BOGOTA',
          'BOGOTÁ, D.C.': 'BOGOTA',
          'MEDELLIN':     'ANTIOQUIA',
          'MEDELLÍN':     'ANTIOQUIA',
          'CALI':         'VALLE DEL CAUCA',
          'BARRANQUILLA': 'ATLANTICO',
          'BUCARAMANGA':  'SANTANDER',
          'CAJICA':       'CUNDINAMARCA',
          'CAJICÁ':       'CUNDINAMARCA',
          'CHIA':         'CUNDINAMARCA',
          'CHÍA':         'CUNDINAMARCA',
          'ZIPAQUIRA':    'CUNDINAMARCA',
          'ZIPAQUIRÁ':    'CUNDINAMARCA',
          'SOACHA':       'CUNDINAMARCA',
        };

        const deptoTarget = mapaRapido[ciudadNorm];

        if (deptoTarget) {
          const optFast = [...provSelect.options].find(
            o => normalizar(o.textContent) === normalizar(deptoTarget)
          );
          if (optFast) {
            const found = await probarDepto(optFast);
            if (found) return;
          }
        }

        for (const opt of deptoOptions) {
          const found = await probarDepto(opt);
          if (found) return;
        }

        console.warn('[AddressManager] No se encontró el departamento para ciudad:', ciudadNorm);
      })();
    }

    buscarCiudadEnDepartamentos(10);
  }

  function _esperarCiudadesYSeleccionar(ciudadNorm) {
    return new Promise(resolve => {
      let intentos = 20;
      const normalizar = str => str.toUpperCase().trim().replace(/\s+/g, ' ')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      const poll = () => {
        const citySelect = document.getElementById('vtl-f-city');
        if (citySelect && citySelect.options.length > 1) {
          const match = [...citySelect.options].find(
            o => normalizar(o.textContent) === ciudadNorm
          );
          if (match) {
            citySelect.value = match.value;
            document.getElementById('vtl-f-city-wrap').style.display = 'block';
          } else {
            console.warn('[AddressManager] Ciudad no encontrada en el select:', ciudadNorm);
          }
          return resolve();
        }
        if (--intentos > 0) setTimeout(poll, 150);
        else {
          console.warn('[AddressManager] Timeout esperando ciudades del departamento');
          resolve();
        }
      };

      poll();
    });
  }

  function _fillForm(addr) {
    _setVal('vtl-f-label',    addr.label);
    _setVal('vtl-f-address1', addr.address1);
    _setVal('vtl-f-address2', addr.address2);
    _setVal('vtl-f-zip',      addr.zip);
    // Guarda los textos exactos para que _prefillProvinceCity los normalice al buscar
    document.getElementById('vtl-f-prov-prefill')?.setAttribute('data-value', addr.province || '');
    document.getElementById('vtl-f-city-prefill')?.setAttribute('data-value', addr.city     || '');
  }

  function _setVal(id, val) {
    const el = document.getElementById(id);
    if (el) el.value = val || '';
  }

  /* ─── Departamentos / Ciudades ───────────────────────────────── */
  function _loadDepartamentosForModal() {
    const sel = document.getElementById('vtl-f-province');
    if (!sel) return;

    if (_departaLoaded && sel.options.length > 1) {
      _prefillProvinceCity();
      return;
    }

    _cargarDepartamentos(sel, _prefillProvinceCity);
  }

  function _prefillProvinceCity() {
    const provInput  = document.getElementById('vtl-f-prov-prefill');
    const provSelect = document.getElementById('vtl-f-province');
    if (!provInput || !provSelect) return;

    const provVal = provInput.getAttribute('data-value');
    if (!provVal) return;

    const normalizar = str => (str || '').toUpperCase().trim().replace(/\s+/g, ' ')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const provNorm = normalizar(provVal);

    const opt = [...provSelect.options].find(o => normalizar(o.textContent) === provNorm);
    if (!opt) {
      console.warn('[AddressManager] Departamento no encontrado para prefill:', provVal);
      return;
    }

    provSelect.value = opt.value;
    provSelect.dispatchEvent(new Event('change'));

    const cityInput = document.getElementById('vtl-f-city-prefill');
    const cityVal   = cityInput?.getAttribute('data-value');
    if (!cityVal) return;

    const cityNorm = normalizar(cityVal);
    let attempts   = 30;

    const poll = () => {
      const citySel = document.getElementById('vtl-f-city');
      if (citySel && citySel.options.length > 1) {
        const cOpt = [...citySel.options].find(
          o => normalizar(o.textContent) === cityNorm
        );
        if (cOpt) {
          citySel.value = cOpt.value;
          const cityWrap = document.getElementById('vtl-f-city-wrap');
          if (cityWrap) cityWrap.style.display = 'block';
        } else {
          console.warn('[AddressManager] Ciudad no encontrada en prefill:', cityVal);
        }
        return;
      }
      if (--attempts > 0) setTimeout(poll, 150);
      else console.warn('[AddressManager] Timeout esperando ciudades (prefill edición)');
    };
    setTimeout(poll, 150);
  }

  function _onProvinceChange(sel) {
    const deptId    = sel.value;
    const cityWrap  = document.getElementById('vtl-f-city-wrap');
    const citySel   = document.getElementById('vtl-f-city');
    if (!cityWrap || !citySel) return;

    citySel.innerHTML = '<option value="">Seleccione una ciudad</option>';
    cityWrap.style.display = 'none';

    if (!deptId) return;

    _ciudades(deptId)
      .then(data => {
        _avisoCatalogo(sel, '');
        if (!data.length) { cityWrap.style.display = 'none'; return; }
        data.forEach(item => {
          const opt       = document.createElement('option');
          opt.value       = item.id;
          opt.textContent = item.ciudad;
          citySel.appendChild(opt);
        });
        cityWrap.style.display = 'block';
      })
      .catch(err => {
        console.error('[AddressManager] Ciudades:', err);
        _avisoCatalogo(sel, 'No pudimos cargar las ciudades.', () => _onProvinceChange(sel));
      });
  }

  /* ─── Submit del formulario ──────────────────────────────────── */
  async function _submitForm(e) {
    e.preventDefault();

    const provSel  = document.getElementById('vtl-f-province');
    const citySel  = document.getElementById('vtl-f-city');
    const province = provSel?.selectedOptions[0]?.textContent || '';
    const city     = citySel?.selectedOptions[0]?.textContent || '';

    if (!province || province === 'Seleccione el departamento') {
      _showFormError('Por favor selecciona el departamento.'); return;
    }

    const cityIsValid = citySel &&
                        citySel.options.length > 1 &&
                        citySel.value !== '' &&
                        city !== 'Seleccione una ciudad';

    if (!cityIsValid) {
      _showFormError('Por favor selecciona una ciudad válida para el departamento elegido.'); return;
    }

    const formData = {
      label:    document.getElementById('vtl-f-label')?.value    || '',
      address1: document.getElementById('vtl-f-address1')?.value || '',
      address2: document.getElementById('vtl-f-address2')?.value || '',
      province,
      city,
      zip:      document.getElementById('vtl-f-zip')?.value   || '',
    };

    const errorEl = document.getElementById('vtl-addr-form-error');
    if (errorEl) errorEl.style.display = 'none';

    if (!formData.label.trim() || formData.label.trim().length < 2) {
      _showFormError('El nombre de la dirección debe tener al menos 2 caracteres.'); return;
    }
    if (!formData.address1.trim() || formData.address1.trim().length < 5) {
      _showFormError('La dirección debe tener al menos 5 caracteres.'); return;
    }
    if (!/^[\w\s.#\-,ÁÉÍÓÚáéíóúñÑ]+$/u.test(formData.address1.trim())) {
      _showFormError('La dirección contiene caracteres no permitidos.'); return;
    }
    if (formData.zip && !/^\d{4,10}$/.test(formData.zip.trim())) {
      _showFormError('El código postal debe ser numérico (4-10 dígitos).'); return;
    }

    const submitBtn = document.getElementById('vtl-addr-submit');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Guardando…'; }

    try {
      let addr;
      if (_currentEditId) {
        addr = await update(_currentEditId, formData);
      } else {
        addr = await create(formData);
        // B74: antes de seleccionarla se avisa que el cliente la escogió (la agenda la da por confirmada)
        document.dispatchEvent(new CustomEvent('vtl:direccion-creada', { detail: { id: addr.id } }));
        select(addr.id);
      }
      _renderList();
    } catch (err) {
      console.error('[AddressManager] submit error', err);
      _showFormError('Ocurrió un error al guardar la dirección.');
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Guardar dirección'; }
    }
  }

  function _showFormError(msg) {
    const el = document.getElementById('vtl-addr-form-error');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  /* ─── Eliminar ───────────────────────────────────────────────── */
  async function _confirmDelete(id) {
    const addr = _addresses.find(a => a.id === id);
    if (!addr) return;
    await remove(id);
    _renderList();
  }

  /* ─── Montar modal en el DOM ─────────────────────────────────── */
  function _mountModal() {
    if (_modalMounted) return;
    _injectStyles();

    const wrapper = document.createElement('div');
    wrapper.id    = 'vtl-addr-modal';
    wrapper.setAttribute('role', 'dialog');
    wrapper.setAttribute('aria-modal', 'true');
    wrapper.setAttribute('aria-labelledby', 'vtl-addr-modal-title');
    wrapper.className = 'vtl-addr-overlay';
    wrapper.style.display = 'none';

    wrapper.innerHTML = `
      <div class="vtl-addr-modal">
        <button class="vtl-addr-modal__close" id="vtl-addr-close" aria-label="Cerrar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
          </svg>
        </button>

        <!-- Vista: lista de direcciones -->
        <div id="vtl-addr-list-view">
          <h2 id="vtl-addr-modal-title" class="vtl-addr-modal__title">Mis direcciones</h2>
          <div id="vtl-addr-list" class="vtl-addr-list"></div>
          <div class="vtl-addr-modal__footer">
            <button class="vtl-addr-btn-new" id="vtl-addr-new">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              Agregar nueva dirección
            </button>
            <button class="vtl-addr-btn-confirm" id="vtl-addr-confirm">
              Confirmar dirección
            </button>
          </div>
        </div>

        <!-- Vista: formulario crear / editar -->
        <div id="vtl-addr-form-view" style="display:none">
          <button class="vtl-addr-back" id="vtl-addr-back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M12 5l-7 7 7 7" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Volver
          </button>
          <h2 id="vtl-addr-form-title" class="vtl-addr-modal__title">Nueva dirección</h2>

          <!-- Hidden inputs para prefill edición -->
          <input type="hidden" id="vtl-f-prov-prefill" data-value="">
          <input type="hidden" id="vtl-f-city-prefill" data-value="">

          <form id="vtl-addr-form" autocomplete="off" novalidate>
            <div class="vtl-addr-field">
              <label class="vtl-addr-label" for="vtl-f-label">Nombre de la dirección *</label>
              <input id="vtl-f-label" class="vtl-addr-input" type="text"
                     placeholder="ej. Casa, Oficina…" required minlength="2">
            </div>
            <div class="vtl-addr-field">
              <label class="vtl-addr-label" for="vtl-f-province">Departamento *</label>
              <select id="vtl-f-province" class="vtl-addr-input" required
                      onchange="window.AddressManager._onProvinceChange(this)">
                <option value="">Seleccione el departamento</option>
              </select>
            </div>
            <div class="vtl-addr-field" id="vtl-f-city-wrap" style="display:none">
              <label class="vtl-addr-label" for="vtl-f-city">Ciudad *</label>
              <select id="vtl-f-city" class="vtl-addr-input" required>
                <option value="">Seleccione una ciudad</option>
              </select>
            </div>
            <div class="vtl-addr-field">
              <label class="vtl-addr-label" for="vtl-f-address1">Dirección de residencia *</label>
              <input id="vtl-f-address1" class="vtl-addr-input" type="text"
                     placeholder="Calle 123 # 45-67" required minlength="5">
            </div>
            <div class="vtl-addr-field">
              <label class="vtl-addr-label" for="vtl-f-address2">Detalle (opcional)</label>
              <input id="vtl-f-address2" class="vtl-addr-input" type="text"
                     placeholder="Apto 201, Torre B…">
            </div>
            <div id="vtl-addr-form-error" class="vtl-addr-form-error" style="display:none"></div>
            <div class="vtl-addr-modal__footer" style="justify-content: center !important;">
              <button type="submit" class="vtl-addr-btn-confirm" id="vtl-addr-submit">
                Guardar dirección
              </button>
            </div>
          </form>
        </div>
      </div>
    `;

    document.body.appendChild(wrapper);

    /* Eventos */
    document.getElementById('vtl-addr-close')
      ?.addEventListener('click', _close);
    document.getElementById('vtl-addr-new')
      ?.addEventListener('click', () => _openForm(null));
    document.getElementById('vtl-addr-back')
      ?.addEventListener('click', _renderList);
    document.getElementById('vtl-addr-confirm')
      ?.addEventListener('click', () => {
        if (!getSelected()) { return; }
        _close();
        _notify();
      });
    document.getElementById('vtl-addr-form')
      ?.addEventListener('submit', _submitForm);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') _close();
    });

    _modalMounted = true;
  }

  /* ─── Estilos del modal ──────────────────────────────────────── */
  function _injectStyles() {
    if (document.getElementById('vtl-addr-styles')) return;
    const style = document.createElement('style');
    style.id    = 'vtl-addr-styles';
    style.textContent = `
      .vtl-addr-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,.55);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }

      .vtl-addr-modal {
        background: #fff;
        border-radius: 20px;
        box-shadow: 0 12px 48px rgba(0,0,0,.18);
        width: 100%;
        max-width: 520px;
        max-height: 90vh;
        overflow-y: auto;
        padding: 36px 28px 28px;
        position: relative;
        scrollbar-width: thin;
        scrollbar-color: #f0a0c8 #f9f9f9;
      }

      .vtl-addr-modal::-webkit-scrollbar { width: 5px; }
      .vtl-addr-modal::-webkit-scrollbar-thumb { background: #f0a0c8; border-radius: 10px; }

      .vtl-addr-modal__close {
        position: absolute;
        top: 14px; right: 16px;
        background: none;
        border: none;
        color: #888;
        cursor: pointer;
        line-height: 1;
        padding: 4px;
        border-radius: 6px;
        transition: color .2s, background .2s;
      }
      .vtl-addr-modal__close:hover { color: #e71578; background: #fff0f7; }

      .vtl-addr-back {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: none;
        border: none;
        color: #888;
        font-size: 13px;
        cursor: pointer;
        padding: 0;
        margin-bottom: 12px;
        transition: color .2s;
      }
      .vtl-addr-back:hover { color: #e71578; }

      .vtl-addr-modal__title {
        font-size: 20px;
        font-weight: 700;
        color: #222;
        margin: 0 0 20px;
        text-align: center;
      }

      .vtl-addr-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-height: 340px;
        overflow-y: auto;
        scrollbar-width: thin;
        padding-right: 2px;
      }

      .vtl-addr-card {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        border: 2px solid #e8e8e8;
        border-radius: 14px;
        padding: 14px 16px;
        cursor: pointer;
        transition: border-color .2s, background .2s;
      }
      .vtl-addr-card:hover { border-color: #e71578; background: #fff7fb; }
      .vtl-addr-card--selected { border-color: #e71578; background: #fff0f7; }

      .vtl-addr-radio {
        width: 18px; height: 18px;
        border-radius: 50%;
        border: 2px solid #ccc;
        flex-shrink: 0;
        margin-top: 3px;
        transition: all .2s;
      }
      .vtl-addr-radio--active {
        border-color: #e71578;
        background: radial-gradient(circle, #e71578 45%, transparent 46%);
      }

      .vtl-addr-card__body { flex: 1; min-width: 0; }
      .vtl-addr-card__label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #222;
        margin-bottom: 3px;
      }
      .vtl-addr-card__text {
        font-size: 13px;
        color: #666;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 280px;
        display: block;
      }

      .vtl-addr-badge {
        display: inline-block;
        font-size: 11px;
        font-weight: 600;
        color: #e71578;
        background: #fff0f7;
        border: 1px solid #f5b8d9;
        border-radius: 20px;
        padding: 1px 8px;
        margin-left: 6px;
        vertical-align: middle;
      }

      .vtl-addr-card__actions {
        display: flex;
        gap: 6px;
        flex-shrink: 0;
      }

      .vtl-addr-btn-icon {
        background: none;
        border: 1.5px solid #e8e8e8;
        border-radius: 8px;
        padding: 5px 7px;
        cursor: pointer;
        color: #888;
        transition: all .2s;
        line-height: 1;
      }
      .vtl-addr-btn-icon:hover { border-color: #e71578; color: #e71578; background: #fff7fb; }
      .vtl-addr-btn-icon--danger:hover { border-color: #f05; color: #f05; background: #fff0f3; }

      .vtl-addr-empty {
        text-align: center;
        padding: 40px 20px;
        color: #aaa;
      }
      .vtl-addr-empty svg { margin-bottom: 12px; opacity: .5; }
      .vtl-addr-empty p { font-size: 14px; margin: 0; }

      .vtl-addr-modal__footer {
        display: flex;
        gap: 12px;
        justify-content: space-between;
        align-items: center;
        margin-top: 20px;
        flex-wrap: wrap;
      }

      .vtl-addr-btn-new {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        background: none;
        border: 2px solid #e71578;
        color: #e71578;
        border-radius: 30px;
        padding: 10px 18px;
        font-size: 13px;
        cursor: pointer;
        transition: all .2s;
      }
      .vtl-addr-btn-new:hover { background: #fff0f7; }

      .vtl-addr-btn-confirm {
        background: #e71578;
        color: #fff;
        border: none;
        border-radius: 30px;
        padding: 12px 28px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity .2s;
        flex-shrink: 0;
      }
      .vtl-addr-btn-confirm:hover { opacity: .88; }
      .vtl-addr-btn-confirm:disabled { opacity: .5; cursor: default; }

      .vtl-addr-field { margin-bottom: 14px; }
      .vtl-addr-label {
        display: block;
        font-size: 13px;
        color: #444;
        margin-bottom: 5px;
        font-weight: 500;
      }
      .vtl-addr-input {
        width: 100%;
        height: 44px;
        border: 2px solid #e8e8e8;
        border-radius: 30px;
        padding: 0 16px;
        font-size: 14px;
        outline: none;
        transition: border-color .2s;
        color: #222;
        background: #fff;
        box-sizing: border-box;
      }
      .vtl-addr-input:focus { border-color: #e71578; }

      .vtl-addr-form-error {
        background: #fff0f3;
        border: 1px solid #f5b8d9;
        color: #c0164a;
        border-radius: 10px;
        padding: 10px 14px;
        font-size: 13px;
        margin-bottom: 12px;
      }

      @media (max-width: 540px) {
        .vtl-addr-modal { padding: 28px 16px 20px; }
        .vtl-addr-card__text { max-width: 160px; }
        .vtl-addr-modal__footer { flex-direction: column; }
        .vtl-addr-btn-new, .vtl-addr-btn-confirm { width: 100%; justify-content: center; }
      }
    `;
    document.head.appendChild(style);
  }

  /* ─── Utilidades UI ──────────────────────────────────────────── */
  function _esc(str) {
    return String(str || '').replace(/[&<>"']/g, c =>
      ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])
    );
  }

  function _formatAddr(addr) {
    return [addr.address1, addr.address2, addr.city, addr.province]
      .filter(Boolean).join(', ');
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 6 — READY QUEUE
      ═══════════════════════════════════════════════════════════════ */

  let _isReady  = false;
  let _readyQ   = [];

  function ready(fn) {
    if (typeof fn !== 'function') return;
    if (_isReady) {
      setTimeout(() => fn(global.AddressManager), 0);
    } else {
      _readyQ.push(fn);
    }
  }

  /**
   * FIX: activa _initDone ANTES de ejecutar los callbacks de ready,
   * para que cualquier _notify() disparado desde ese punto en adelante
   * sí llegue a los suscriptores.
   */
  function _flushReadyQueue() {
    _initDone = true;   // a partir de aquí _notify() ya funciona
    _isReady  = true;
    const api = global.AddressManager;
    _readyQ.forEach(fn => {
      try { fn(api); } catch (e) { console.error('[AddressManager] ready callback error', e); }
    });
    _readyQ = [];
  }

  /* ═══════════════════════════════════════════════════════════════
      SECCIÓN 7 — INIT
      ═══════════════════════════════════════════════════════════════ */
  async function init() {
    await _loadAddresses();
    if (_isLoggedIn() && _lsGet().some(a => a.source === 'local')) {
      await syncOnLogin();
    }
    _flushReadyQueue();
  }

  document.addEventListener('DOMContentLoaded', init);

  /* ─── API pública ───────────────────────────────────────────── */
  const _preExistingQueue = (global.AddressManager && Array.isArray(global.AddressManager.ready))
    ? global.AddressManager.ready.slice()
    : [];

  global.AddressManager = {
    /* Gestión de datos */
    getAll,
    getSelected,
    select,
    create,
    update,
    remove,
    syncOnLogin,
    /* Modal */
    open,
    /* Filtro de ciudad */
    setCiudadFiltro,
    /* Eventos */
    onSelect,
    offSelect,
    /* Init diferido */
    ready,
    /* Expuesto para uso en HTML inline del modal */
    _onProvinceChange,
  };

  _preExistingQueue.forEach(fn => _readyQ.push(fn));

})(window);